<script setup lang="ts">
import { onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

import RouteHelper from "@/common/route-helper";
import { QUERY_CENTLEX_ELEMENT_ID_KEY } from "@/router/query-params";
import type {
  CentlexAntonym,
  CentlexMeaning,
  CentlexSynonym,
  CentlexWordFormation
} from "@/types/article-formats/centlex";

import ArticleLink from "./ArticleLink.vue";
import ArticleLinkableListSection from "./ArticleLinkableListSection.vue";
import ArticleListSection from "./ArticleListSection.vue";
import ArticleSection from "./ArticleSection.vue";

const props = defineProps<{
  homonymId: number | null;
  meanings: CentlexMeaning[];
}>();

const getId = (meaning: CentlexMeaning) => (props.homonymId ?? 0) + "-" + (meaning.polysemId ?? 0);

const getLinkableListFromAntonyms = (antonyms: CentlexAntonym[]) =>
  antonyms.map((antonym) => ({
    isLink: antonym.isLemma,
    text: antonym.antonym
  }));

const getLinkableListFromSynonyms = (synonyms: CentlexSynonym[]) =>
  synonyms.map((synonym) => ({
    isLink: synonym.isLemma,
    text: synonym.synonym
  }));

const getLinkableListFromWordFormations = (wordFormations: CentlexWordFormation[]) =>
  wordFormations.map((wordFormation) => ({
    isLink: wordFormation.isLemma,
    text: wordFormation.wordFormation
  }));

onMounted(() => {
  const elementId = useRoute().query[QUERY_CENTLEX_ELEMENT_ID_KEY];
  if (!elementId) {
    return;
  }

  const routeHelper = new RouteHelper(useRouter());
  routeHelper.replaceQueryParam({ [QUERY_CENTLEX_ELEMENT_ID_KEY]: undefined });

  const element = document.getElementById(elementId as string);
  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: "smooth" });
});
</script>

<template>
  <div class="indentation-container">
    <div v-for="(meaning, meaningIndex) in props.meanings" :id="getId(meaning)" :key="meaningIndex">
      <div :class="{ 'indentation-container': meaning.polysemId }">
        <span
          v-if="meaning.polysemId"
          class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
        >
          {{ meaning.polysemId }}.
        </span>
        <span
          v-if="meaning.sublemma"
          class="article-content__text article-content__text--primary article-content__text--prominent"
        >
          {{ meaning.sublemma }}<br />
        </span>

        <ArticleSection
          v-if="meaning.grammaticalRemark || meaning.grammaticalReference"
          :label="meaning.grammaticalRemark ? `Grammatisk anmærkning` : undefined"
        >
          {{ meaning.grammaticalRemark ?? "" }}
          <template v-if="meaning.grammaticalReference">
            <span v-if="meaning.grammaticalReference.baseForm === ''">
              Denne skrivemåde anbefales ikke, brug i stedet
            </span>
            <span v-else>Bøjning af grundformen</span>

            <ArticleLink
              :query="meaning.grammaticalReference.grammaticalReference"
              :homonym-id="meaning.grammaticalReference.homonymId"
              :polysem-id="meaning.grammaticalReference.polysemId"
              class="pl-1"
            />
          </template>
        </ArticleSection>

        <ArticleSection v-if="meaning.meaning" label="Betydning">
          {{ meaning.meaning }}
        </ArticleSection>

        <ArticleSection v-if="meaning.lexicalRemarks.length > 0" label="Leksikalsk anmærkning">
          {{ meaning.lexicalRemarks.join(" ") }}
        </ArticleSection>

        <ArticleSection
          v-if="meaning.textProductionRemarks.length > 0"
          label="Anmærkning til tekstproduktion"
        >
          {{ meaning.textProductionRemarks.join(" ") }}
        </ArticleSection>

        <ArticleLinkableListSection
          v-if="meaning.synonyms.length > 0"
          label="Synonymer"
          :list="getLinkableListFromSynonyms(meaning.synonyms)"
        />

        <ArticleSection v-if="meaning.synonymRemarks.length > 0" label="Anmærkning til synonym">
          {{ meaning.synonymRemarks.join(" ") }}
        </ArticleSection>

        <ArticleLinkableListSection
          v-if="meaning.antonyms.length > 0"
          label="Antonymer"
          :list="getLinkableListFromAntonyms(meaning.antonyms)"
        />

        <ArticleListSection
          v-if="meaning.collocations.length > 0"
          label="Kollokationer"
          :list="meaning.collocations"
        />

        <ArticleListSection
          v-if="meaning.examples.length > 0"
          label="Eksempler"
          :list="meaning.examples"
        />

        <ArticleSection v-if="meaning.idioms.length > 0" label="Idiomer">
          <ul class="article-content__group-list">
            <li
              v-for="(idiom, idiomIndex) in meaning.idioms"
              :key="idiomIndex"
              class="article-content__group-list-item article-content__group-list-item--inline"
            >
              <span class="article-content__text article-content__text--light pr-1">
                {{ idiom.idiom }}
              </span>
              <i class="article-content__text article-content__text--comment">
                ({{ idiom.explanation }})
              </i>
            </li>
          </ul>
        </ArticleSection>

        <ArticleListSection
          v-if="meaning.sentences.length > 0"
          label="Ordsprog o.l."
          :list="meaning.sentences"
        />

        <ArticleLinkableListSection
          v-if="meaning.wordFormations.length > 0"
          label="Orddannelser"
          :list="getLinkableListFromWordFormations(meaning.wordFormations)"
        />

        <ArticleSection
          v-if="meaning.lexicalReferences.length > 0"
          label="Se også"
          class="article-content__inline-list"
        >
          <span
            v-for="(lexicalReference, lexicalReferenceIndex) in meaning.lexicalReferences"
            :key="lexicalReferenceIndex"
          >
            <ArticleLink
              :query="lexicalReference.lexicalReference"
              :homonym-id="lexicalReference.homonymId"
              :polysem-id="lexicalReference.polysemId"
            />
          </span>
        </ArticleSection>

        <ArticleSection v-if="meaning.links.length > 0" label="Hjemmeside">
          <ul class="article-content__group-list">
            <li
              v-for="(link, linkIndex) in meaning.links"
              :key="linkIndex"
              class="article-content__group-list-item article-content__group-list-item--inline"
            >
              <a
                :href="link.link"
                class="article-content__text article-content__text--light pr-1"
                target="_blank"
              >
                {{ link.title ? link.title : link.link }}
              </a>
            </li>
          </ul>
        </ArticleSection>
      </div>
    </div>
  </div>
</template>
